// packages/web/src/hooks/useCart.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cartService, CartItemInput } from '@/services/cart.service';
import { productService } from '@/services/product.service';
import { Product } from '@bookstore/shared';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Enhanced cart item with full book details
 */
export interface CartItemWithDetails extends CartItemInput {
    product: Product;
}

/**
 * Get user's cart items (IDs + quantities)
 */
export const useCartItems = () => {
    const { user } = useAuth();
    return useQuery<CartItemInput[]>({
        queryKey: ['cart-items', user?.uid],
        queryFn: () => cartService.getCart(user!.uid),
        enabled: !!user,
        staleTime: 30 * 1000,
        placeholderData: [],
    });
};

/**
 * Get cart with full book details
 */
export const useCartWithDetails = () => {
    const { user } = useAuth();
    const { data: cartItems = [], isLoading: itemsLoading } = useCartItems();

    return useQuery<CartItemWithDetails[]>({
        queryKey: ['cart-with-details', user?.uid, cartItems],
        queryFn: async () => {
            if (!cartItems || cartItems.length === 0) {
                return [];
            }

            // Fetch all products in parallel using allSettled to avoid failing on deleted products
            const productPromises = cartItems.map((item) => 
                productService.getProductById(item.productId).catch(err => {
                    console.warn(`Failed to fetch product ${item.productId}:`, err.message);
                    return null;
                })
            );
            const products = await Promise.all(productPromises);

            // Combine cart items with product details, filtering out deleted products
            const cartWithDetails: CartItemWithDetails[] = [];
            const invalidProductIds: string[] = [];

            for (let i = 0; i < cartItems.length; i++) {
                const product = products[i];
                if (product) {
                    cartWithDetails.push({
                        ...cartItems[i],
                        product,
                    });
                } else {
                    // Track deleted/invalid products
                    invalidProductIds.push(cartItems[i].productId);
                    console.warn(`Product ${cartItems[i].productId} no longer exists, removing from cart`);
                }
            }

            // Auto-clean invalid products from cart in background
            if (invalidProductIds.length > 0 && user?.uid) {
                Promise.all(
                    invalidProductIds.map(productId => 
                        cartService.removeFromCart(user.uid, productId).catch(err => 
                            console.error('Failed to remove invalid product from cart:', err)
                        )
                    )
                ).then(() => {
                    toast.info(`Removed ${invalidProductIds.length} unavailable item(s) from cart`);
                });
            }

            return cartWithDetails;
        },
        enabled: !!user && cartItems.length > 0,
        staleTime: 1 * 60 * 1000, // 1 minute - short to get fresh prices
        placeholderData: [],
    });
};

/**
 * Add to cart
 */
export const useAddToCart = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ productId, quantity = 1 }: { productId: string; quantity?: number }) =>
            cartService.addToCart(user!.uid,productId, quantity),

        // Optimistic update
        onMutate: async ({ productId, quantity = 1 }) => {
            await queryClient.cancelQueries({ queryKey: ['cart-items', user?.uid] });
            const previousCart = queryClient.getQueryData<CartItemInput[]>(['cart-items', user?.uid]);

            queryClient.setQueryData<CartItemInput[]>(['cart-items', user?.uid], (old = []) => {
                const existingIndex = old.findIndex((item) => item.productId === productId);

                if (existingIndex !== -1) {
                    // Update quantity
                    const updated = [...old];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        quantity: updated[existingIndex].quantity + quantity,
                    };
                    return updated;
                } else {
                    // Add new item
                    return [
                        ...old,
                        {
                            productId,
                            quantity,
                            addedAt: new Date() as any,
                        },
                    ];
                }
            });

            return { previousCart };
        },

        onError: (err, variables, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart-items', user?.uid], context.previousCart);
            }
            toast.error('Failed to add to cart');
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart-items', user?.uid] });
            queryClient.invalidateQueries({ queryKey: ['cart-with-details', user?.uid] });
            toast.success('Added to cart');
        },
    });
};

/**
 * Update quantity
 */
export const useUpdateQuantity = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
            cartService.updateQuantity(user!.uid,productId, quantity),

        // Optimistic update
        onMutate: async ({ productId, quantity }) => {
            await queryClient.cancelQueries({ queryKey: ['cart-items', user?.uid] });

            const previousCart = queryClient.getQueryData<CartItemInput[]>(['cart-items', user?.uid]);

            queryClient.setQueryData<CartItemInput[]>(['cart-items', user?.uid], (old = []) => {
                if (quantity <= 0) {
                    return old.filter((item) => item.productId !== productId);
                }
                return old.map((item) => (item.productId === productId ? { ...item, quantity } : item));
            });

            return { previousCart };
        },

        onError: (err, variables, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart-items', user?.uid], context.previousCart);
            }
            toast.error('Failed to update quantity');
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart-items', user?.uid] });
            queryClient.invalidateQueries({ queryKey: ['cart-with-details', user?.uid] });
        },
    });
};

/**
 * Remove from cart
 */
export const useRemoveFromCart = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (productId: string) => cartService.removeFromCart(user!.uid, productId),

        // Optimistic update
        onMutate: async (productId) => {
            await queryClient.cancelQueries({ queryKey: ['cart-items', user?.uid] });

            const previousCart = queryClient.getQueryData<CartItemInput[]>(['cart-items', user?.uid]);

            queryClient.setQueryData<CartItemInput[]>(['cart-items', user?.uid], (old = []) => {
                return old.filter((item) => item.productId !== productId);
            });

            return { previousCart };
        },

        onError: (err, productId, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart-items', user?.uid], context.previousCart);
            }
            toast.error('Failed to remove from cart');
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart-items', user?.uid] });
            queryClient.invalidateQueries({ queryKey: ['cart-with-details', user?.uid] });
            toast.success('Removed from cart');
        },
    });
};

/**
 * Clear cart
 */
export const useClearCart = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => cartService.clearCart(user!.uid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart-items'] });
            queryClient.invalidateQueries({ queryKey: ['cart-with-details'] });
        },
        onError: () => {
            toast.error('Failed to clear cart');
        },
    });
};

/**
 * Main hook - Zustand-like API
 */
export const useCart = () => {
    const { user } = useAuth();
    const { data: cartItems = [] } = useCartItems();
    const { data: cartWithDetails = [] } = useCartWithDetails();

    const addMutation = useAddToCart();
    const updateMutation = useUpdateQuantity();
    const removeMutation = useRemoveFromCart();

    // Calculate totals
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cartWithDetails.reduce((sum, item) => {
        const price = item.product.discountedPrice || item.product.price;
        return sum + price * item.quantity;
    }, 0);

    return {
        // State
        cartItems,
        cartWithDetails,
        totalItems,
        totalPrice,

        // Checkers
        isInCart: (productId: string) => cartItems.some((item) => item.productId === productId),
        getItemQuantity: (productId: string) =>
            cartItems.find((item) => item.productId === productId)?.quantity || 0,

        // Actions
        addToCart: (productId: string, quantity = 1) => {
            if (!user) {
                toast.error('Please login to add to cart');
                return;
            }
            addMutation.mutate({ productId, quantity });
        },

        updateQuantity: (productId: string, quantity: number) => {
            if (!user) return;
            updateMutation.mutate({ productId, quantity });
        },

        removeFromCart: (productId: string) => {
            if (!user) return;
            removeMutation.mutate(productId);
        },

        // Loading states
        isPending:
            addMutation.isPending || updateMutation.isPending || removeMutation.isPending,
    };
};